/**
 * The action log — the canonical replay artifact (SPEC.md §4.2, CONTEXT.md).
 *
 * Deck lists + seed + every choice made, all plain data. Emitted events are *derived* and
 * are never stored here; replaying the log through the engine reproduces the game exactly,
 * events included. This is also the format the golden-replay suite (SPEC.md §9) records.
 */
import type { CardCatalog } from './cards.ts';
import { clone } from './clone.ts';
import { apply, EngineError } from './engine.ts';
import type { GameState } from './state.ts';
import type { GameSetup } from './setup.ts';
import { createGame } from './setup.ts';

export interface ActionLog {
  readonly setup: GameSetup;
  /** Canonical action keys, in the order they were applied. */
  readonly choices: string[];
}

export function startLog(setup: GameSetup): ActionLog {
  return { setup, choices: [] };
}

export function recordChoice(log: ActionLog, key: string): void {
  log.choices.push(key);
}

/** Replay a log to its final state. */
export function replay(log: ActionLog, catalog: CardCatalog): GameState {
  const state = createGame(log.setup, catalog);
  for (const [index, key] of log.choices.entries()) {
    if (state.result !== null) {
      throw new EngineError(`Action log continues past the end of the game at choice ${index}`);
    }
    apply(state, key);
  }
  return state;
}

/**
 * Replay a log, capturing an independent snapshot after every step. `states[0]` is the
 * freshly dealt game, `states[n]` the state after the log's nth choice — the sequence the
 * replay-determinism test and the goldens' hash chain compare.
 */
export function replayStates(log: ActionLog, catalog: CardCatalog): GameState[] {
  const state = createGame(log.setup, catalog);
  const states: GameState[] = [clone(state)];
  for (const key of log.choices) {
    apply(state, key);
    states.push(clone(state));
  }
  return states;
}
